import React, { useState, useEffect } from 'react';
import { Ruler, Copy, Check, ArrowRightLeft } from 'lucide-react';

type UnitCategory = 'length' | 'weight' | 'temperature' | 'volume' | 'area' | 'speed' | 'time' | 'data';

interface UnitDefinition {
  name: string;
  toBase: (value: number) => number;
  fromBase: (value: number) => number;
}

const unitDefinitions: Record<UnitCategory, Record<string, UnitDefinition>> = {
  length: {
    meter: { name: 'Meters (m)', toBase: (v) => v, fromBase: (v) => v },
    kilometer: { name: 'Kilometers (km)', toBase: (v) => v * 1000, fromBase: (v) => v / 1000 },
    centimeter: { name: 'Centimeters (cm)', toBase: (v) => v / 100, fromBase: (v) => v * 100 },
    millimeter: { name: 'Millimeters (mm)', toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
    mile: { name: 'Miles (mi)', toBase: (v) => v * 1609.344, fromBase: (v) => v / 1609.344 },
    yard: { name: 'Yards (yd)', toBase: (v) => v * 0.9144, fromBase: (v) => v / 0.9144 },
    foot: { name: 'Feet (ft)', toBase: (v) => v * 0.3048, fromBase: (v) => v / 0.3048 },
    inch: { name: 'Inches (in)', toBase: (v) => v * 0.0254, fromBase: (v) => v / 0.0254 },
  },
  weight: {
    kilogram: { name: 'Kilograms (kg)', toBase: (v) => v, fromBase: (v) => v },
    gram: { name: 'Grams (g)', toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
    milligram: { name: 'Milligrams (mg)', toBase: (v) => v / 1000000, fromBase: (v) => v * 1000000 },
    pound: { name: 'Pounds (lb)', toBase: (v) => v * 0.453592, fromBase: (v) => v / 0.453592 },
    ounce: { name: 'Ounces (oz)', toBase: (v) => v * 0.0283495, fromBase: (v) => v / 0.0283495 },
    ton: { name: 'Metric Tons (t)', toBase: (v) => v * 1000, fromBase: (v) => v / 1000 },
    stone: { name: 'Stone (st)', toBase: (v) => v * 6.35029, fromBase: (v) => v / 6.35029 },
  },
  temperature: {
    celsius: { name: 'Celsius (°C)', toBase: (v) => v, fromBase: (v) => v },
    fahrenheit: { name: 'Fahrenheit (°F)', toBase: (v) => (v - 32) * 5/9, fromBase: (v) => v * 9/5 + 32 },
    kelvin: { name: 'Kelvin (K)', toBase: (v) => v - 273.15, fromBase: (v) => v + 273.15 },
  },
  volume: {
    liter: { name: 'Liters (L)', toBase: (v) => v, fromBase: (v) => v },
    milliliter: { name: 'Milliliters (mL)', toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
    gallon_us: { name: 'US Gallons', toBase: (v) => v * 3.78541, fromBase: (v) => v / 3.78541 },
    gallon_uk: { name: 'UK Gallons', toBase: (v) => v * 4.54609, fromBase: (v) => v / 4.54609 },
    quart: { name: 'Quarts (qt)', toBase: (v) => v * 0.946353, fromBase: (v) => v / 0.946353 },
    pint: { name: 'Pints (pt)', toBase: (v) => v * 0.473176, fromBase: (v) => v / 0.473176 },
    cup: { name: 'Cups', toBase: (v) => v * 0.236588, fromBase: (v) => v / 0.236588 },
    fluid_oz: { name: 'Fluid Ounces (fl oz)', toBase: (v) => v * 0.0295735, fromBase: (v) => v / 0.0295735 },
  },
  area: {
    sqmeter: { name: 'Square Meters (m²)', toBase: (v) => v, fromBase: (v) => v },
    sqkilometer: { name: 'Square Kilometers (km²)', toBase: (v) => v * 1000000, fromBase: (v) => v / 1000000 },
    sqfoot: { name: 'Square Feet (ft²)', toBase: (v) => v * 0.092903, fromBase: (v) => v / 0.092903 },
    sqyard: { name: 'Square Yards (yd²)', toBase: (v) => v * 0.836127, fromBase: (v) => v / 0.836127 },
    sqmile: { name: 'Square Miles (mi²)', toBase: (v) => v * 2589988, fromBase: (v) => v / 2589988 },
    acre: { name: 'Acres', toBase: (v) => v * 4046.86, fromBase: (v) => v / 4046.86 },
    hectare: { name: 'Hectares (ha)', toBase: (v) => v * 10000, fromBase: (v) => v / 10000 },
  },
  speed: {
    mps: { name: 'Meters/second (m/s)', toBase: (v) => v, fromBase: (v) => v },
    kmph: { name: 'Kilometers/hour (km/h)', toBase: (v) => v / 3.6, fromBase: (v) => v * 3.6 },
    mph: { name: 'Miles/hour (mph)', toBase: (v) => v * 0.44704, fromBase: (v) => v / 0.44704 },
    knot: { name: 'Knots (kn)', toBase: (v) => v * 0.514444, fromBase: (v) => v / 0.514444 },
    fps: { name: 'Feet/second (ft/s)', toBase: (v) => v * 0.3048, fromBase: (v) => v / 0.3048 },
  },
  time: {
    second: { name: 'Seconds (s)', toBase: (v) => v, fromBase: (v) => v },
    millisecond: { name: 'Milliseconds (ms)', toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
    minute: { name: 'Minutes (min)', toBase: (v) => v * 60, fromBase: (v) => v / 60 },
    hour: { name: 'Hours (h)', toBase: (v) => v * 3600, fromBase: (v) => v / 3600 },
    day: { name: 'Days (d)', toBase: (v) => v * 86400, fromBase: (v) => v / 86400 },
    week: { name: 'Weeks (wk)', toBase: (v) => v * 604800, fromBase: (v) => v / 604800 },
    month: { name: 'Months (avg)', toBase: (v) => v * 2629746, fromBase: (v) => v / 2629746 },
    year: { name: 'Years (yr)', toBase: (v) => v * 31556952, fromBase: (v) => v / 31556952 },
  },
  data: {
    byte: { name: 'Bytes (B)', toBase: (v) => v, fromBase: (v) => v },
    kilobyte: { name: 'Kilobytes (KB)', toBase: (v) => v * 1024, fromBase: (v) => v / 1024 },
    megabyte: { name: 'Megabytes (MB)', toBase: (v) => v * 1048576, fromBase: (v) => v / 1048576 },
    gigabyte: { name: 'Gigabytes (GB)', toBase: (v) => v * 1073741824, fromBase: (v) => v / 1073741824 },
    terabyte: { name: 'Terabytes (TB)', toBase: (v) => v * 1099511627776, fromBase: (v) => v / 1099511627776 },
    bit: { name: 'Bits (b)', toBase: (v) => v / 8, fromBase: (v) => v * 8 },
    kilobit: { name: 'Kilobits (Kb)', toBase: (v) => v * 128, fromBase: (v) => v / 128 },
    megabit: { name: 'Megabits (Mb)', toBase: (v) => v * 131072, fromBase: (v) => v / 131072 },
  },
};

const categoryLabels: Record<UnitCategory, string> = {
  length: 'Length',
  weight: 'Weight/Mass',
  temperature: 'Temperature',
  volume: 'Volume',
  area: 'Area',
  speed: 'Speed',
  time: 'Time',
  data: 'Data Storage',
};


const UnitConverter: React.FC = () => {
  const [category, setCategory] = useState<UnitCategory>('length');
  const [fromUnit, setFromUnit] = useState('meter');
  const [toUnit, setToUnit] = useState('foot');
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const units = Object.keys(unitDefinitions[category]);
    setFromUnit(units[0]);
    setToUnit(units[1] || units[0]);
  }, [category]);

  useEffect(() => {
    convert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromValue, fromUnit, toUnit, category]);

  const convert = () => {
    const num = parseFloat(fromValue);
    if (isNaN(num)) {
      setToValue('');
      return;
    }
    const baseValue = unitDefinitions[category][fromUnit].toBase(num);
    const result = unitDefinitions[category][toUnit].fromBase(baseValue);
    setToValue(result.toPrecision(10).replace(/\.?0+$/, ''));
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  const copyResult = async () => {
    if (!toValue) return;
    await navigator.clipboard.writeText(toValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Unit Converter</h2>
        <p className="text-gray-600">Convert between different units of measurement</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Category Selection */}
        <div className="bg-gray-50 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">Category</label>
          <div className="grid grid-cols-4 gap-2">
            {(Object.keys(categoryLabels) as UnitCategory[]).map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  category === cat
                    ? 'bg-cyan-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {categoryLabels[cat]}
              </button>
            ))}
          </div>
        </div>

        {/* Converter */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-4 items-end">
            {/* From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                {Object.entries(unitDefinitions[category]).map(([key, unit]) => (
                  <option key={key} value={key}>{unit.name}</option>
                ))}
              </select>
              <input
                type="number"
                value={fromValue}
                onChange={(e) => setFromValue(e.target.value)}
                className="w-full px-4 py-3 text-xl font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Enter value"
              />
            </div>

            {/* Swap Button */}
            <button
              onClick={swapUnits}
              className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors self-center mb-2"
            >
              <ArrowRightLeft className="w-5 h-5 text-gray-600" />
            </button>

            {/* To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                {Object.entries(unitDefinitions[category]).map(([key, unit]) => (
                  <option key={key} value={key}>{unit.name}</option>
                ))}
              </select>
              <div className="relative">
                <input
                  type="text"
                  value={toValue}
                  readOnly
                  className="w-full px-4 py-3 text-xl font-mono bg-gray-50 border border-gray-300 rounded-lg"
                  placeholder="Result"
                />
                <button
                  onClick={copyResult}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700"
                >
                  {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Reference */}
        <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-100">
          <div className="flex items-start gap-3">
            <Ruler className="w-5 h-5 text-cyan-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-cyan-900 mb-2">Quick Reference</h4>
              <p className="text-sm text-cyan-700">
                {category === 'length' && '1 mile = 1.609 km • 1 foot = 30.48 cm • 1 inch = 2.54 cm'}
                {category === 'weight' && '1 pound = 0.454 kg • 1 ounce = 28.35 g • 1 stone = 6.35 kg'}
                {category === 'temperature' && '°F = °C × 9/5 + 32 • K = °C + 273.15'}
                {category === 'volume' && '1 gallon (US) = 3.785 L • 1 cup = 236.6 mL'}
                {category === 'area' && '1 acre = 4,047 m² • 1 hectare = 10,000 m²'}
                {category === 'speed' && '1 mph = 1.609 km/h • 1 knot = 1.852 km/h'}
                {category === 'time' && '1 day = 86,400 seconds • 1 year ≈ 31.56 million seconds'}
                {category === 'data' && '1 KB = 1,024 bytes • 1 MB = 1,024 KB • 1 GB = 1,024 MB'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitConverter;
